/* Core & libraries */
import React, { useEffect, useState } from 'react';
import { useTheme } from 'react-jss';
import { useLocation, useNavigate } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';

/* Helpers, constants, custom methods, internal libraries */
import { usePeopleService } from '../../../services/peopleService';
import { useAnalytics } from "../../../hooks/useAnalytics";
import { PAGE_TITLES } from "../../../constants/appConstants";
import { useHasPermission } from 'hooks/useHasPermission';
import { ReactComponent as DownloadIcon } from "../../../resources/images/download-02.svg";

/* Components */
import GridFilters from '../../ucgrid/filters';
import PeopleGrid from './peopleGrid';
import UCButton from 'components/buttons';

/* Styles */
import styles from './styles.js';

const PeopleTab = () => {
  const theme = useTheme();
  const classes = styles({ theme });
  const location = useLocation();
  const navigate = useNavigate();
  const { initAnalytics } = useAnalytics();
  const { peopleResponse, getPeopleList, deletePeople, searchPeople, updatePeople, gridActionPermision, downloadPeopleGridData } = usePeopleService();
  const { checkPermission } = useHasPermission();
  const noActivePeopleData = peopleResponse?.results?.resultTotal <= 0

  const [gridOptions, setGridOptions] = useState(() => {
    const initialPage = location.pathname.includes('/people')
      ? new URLSearchParams(location.search).get('page') || 1
      : 1;
    return {
      page: +initialPage,
      filters: {
        ROLE: [],
      },
    };
  });

  useEffect(() => {
    const initUtagData = {
      channel: 'content',
      pageName: PAGE_TITLES["people"]
    };
    initAnalytics(initUtagData, 'init call', 'view')
  }, []);

  useEffect(() => {
    getPeopleList(gridOptions, "ACTIVE");
    gridOptions.page > 1 ? navigate(`/app/people?page=${gridOptions.page}`) : navigate(`/app/people`)
  }, [gridOptions]);

  useEffect(() => {
    const pageFromURL = location.pathname.includes('/people')
      ? new URLSearchParams(location.search).get('page') || 1
      : 1;
    if (+pageFromURL !== gridOptions.page) {
      setGridOptions({ ...gridOptions, page: pageFromURL });
    }
  }, [location.search]);

  const handleDeletePeople = async (entityId) => {
    const isLastUserOnPage = peopleResponse?.results?.dataItems?.length === 1;

    const response = await deletePeople(entityId);

    if (!response) return false;

    const nextPage =
      isLastUserOnPage && gridOptions.page > 1
        ? gridOptions.page - 1
        : gridOptions.page;

    setGridOptions(prev => ({
      ...prev,
      page: nextPage
    }));

    return true;
  };

  const handleUpdatePeople = async (values) => {
    const response = await updatePeople(values);
    if (response) {
      getPeopleList(gridOptions, "ACTIVE");
      return true;
    }
    return false;
  };

  const handleDownloadActivePeopleGrid = () => {
    downloadPeopleGridData("ACTIVE")
  }

  return (
    <div>
      <div className={classes.filtersContainer}>
        <GridFilters
          response={peopleResponse}
          gridOptions={gridOptions}
          setGridOptions={setGridOptions}
          search={term => searchPeople(term, "ACTIVE")}
          type='PEOPLE'
          disabled={Object.keys(peopleResponse).length === 0}
          className={classes.autocomplete}
        />
        {checkPermission('F_USERS_REPORT_DOWNLOAD') && (
          <UCButton
            onClick={handleDownloadActivePeopleGrid}
            type="default"
            text={<FormattedMessage id="people.grids.download" />}
            className={`${classes.downloadPeopleBtn} ${noActivePeopleData ? `${classes.downloadPeopleDisabledBtn}` : ``}`}
            icon={<DownloadIcon/>}
            loadingStateStyling={classes.downloadLoadingState}
            disabled={noActivePeopleData}
            spinnerClassName={classes.spinner}
            spinner={{
              name: "downloadActivePeopleGridData",
              size: 20
            }}
          />
        )}
      </div>
      <PeopleGrid
        peopleResponse={peopleResponse}
        gridOptions={gridOptions}
        setGridOptions={setGridOptions}
        deletePeople={handleDeletePeople}
        updatePeople={handleUpdatePeople}
        gridActionPermision={gridActionPermision}
      />
    </div>
  );
};

export default PeopleTab;
